import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import { Box } from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import GlobalShortcuts, {
	type GlobalShortcutsItem,
	useGlobalShortcutsData,
} from '@atlassian/global-shortcuts';

import type { GlobalSideNavContentProps, NavItemConfig } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import {
	buildSideNavMenuData,
	SideNavDataProvider,
} from '../../controllers/side-nav-data-provider';
import { useUserMenuData } from '../../controllers/user-state/user-menu-data-provider';
import { GLOBAL_SIDE_NAV_TEST_ID, PEEK_TEST_ID, SHORTCUTS_TEST_ID } from '../constants';
import makeForYouNavItem from '../for-you';
import { SideNavigationLayout } from '../layout';
import { messages } from '../messages';
import MoreMenu from '../more-menu';
import makeRecentNavItem from '../recent';
import makeStarredNavItem from '../starred';

export function GlobalSideNavContent(props: GlobalSideNavContentProps) {
	const {
		app,
		bottomElem,
		cloudId,
		disableItemCustomization = false,
		items,
		isShortcutsStatic,
		forYou,
		orgId,
		postItemsElem,
		postMainElem,
		shortcuts,
		topElem,
	} = props;

	const { formatMessage } = useIntl();

	const { userMenuData, setUserMenuData } = useUserMenuData();

	// Prepare global items
	const globalItems = React.useMemo(() => {
		const items = new Map<string, NavItemConfig>();
		if (!forYou.hideForYou) {
			items.set(
				forYou.forYouMenuItemId ?? 'global.sidebar.forYou',
				makeForYouNavItem({
					href: forYou.forYouUrl,
					isSelected: forYou.forYouIsSelected,
					onClick: forYou.forYouOnClick,
				}),
			);
		}
		if (!forYou.hideRecents) {
			items.set(
				forYou.recentsMenuItemId ?? 'global.sidebar.recent',
				makeRecentNavItem({
					useRecentItems: forYou.useRecentItems,
					searchPlaceholder: formatMessage(messages.recentsSearchPlaceholder),
					searchable: forYou.recentsIsSearchable,
					filters: forYou.recentsFilters,
					viewAllHref: forYou.viewAllRecentsUrl,
					viewAllOnClick: forYou.viewAllRecentsOnClick,
				}),
			);
		}
		if (!forYou.hideStarred) {
			items.set(
				forYou.starredMenuItemId ?? 'global.sidebar.starred',
				makeStarredNavItem({
					useStarredItems: forYou.useStarredItems,
					searchable: forYou.starredIsSearchable,
					filters: forYou.starredFilters,
					viewAllHref: forYou.viewAllStarredUrl,
					viewAllOnClick: forYou.viewAllStarredOnClick,
				}),
			);
		}
		return items;
	}, [forYou, formatMessage]);

	const allNavItems = React.useMemo(
		() => ({ ...Object.fromEntries(globalItems), ...items }),
		[globalItems, items],
	);

	const renderNavItem = React.useCallback(
		(id: string) => <NavItem key={id} item={allNavItems[id]} />,
		[allNavItems],
	);

	// Build the sideNavData object, which we will store in a context.
	// TODO: Do we even need a context if all of the components that need this
	//  data are direct-children?
	const sideNavMenuData = useMemo(() => {
		const navData = buildSideNavMenuData(
			Object.keys(allNavItems),
			shortcuts ? Object.keys(shortcuts) : [],
			userMenuData,
			disableItemCustomization,
		);
		return { ...navData, setUserMenuData };
	}, [allNavItems, disableItemCustomization, setUserMenuData, shortcuts, userMenuData]);

	const hydratedShortcuts = useGlobalShortcutsData({
		shortcutsConfig: shortcuts,
		cloudId,
		orgId,
		isShortcutsStatic,
	});

	const shortcutItems = React.useMemo(() => {
		return sideNavMenuData.visibleShortcutMenuIds.reduce(
			(acc: GlobalShortcutsItem[], id: string) => {
				const shortcut = hydratedShortcuts?.[id];
				if (shortcut) {
					acc.push(shortcut);
				}
				return acc;
			},
			[],
		);
	}, [hydratedShortcuts, sideNavMenuData.visibleShortcutMenuIds]);

	const [peekedItemId, setPeekedItemId] = React.useState<string | null>(null);

	const postMain = (
		<Stack space="space.100">
			{postItemsElem}
			{shortcutItems.length ? (
				<GlobalShortcuts
					shortcutItems={shortcutItems}
					cloudId={cloudId}
					orgId={orgId}
					product={app}
					promoteSite
					testId={SHORTCUTS_TEST_ID}
					forceStatic={true}
				/>
			) : null}
			{peekedItemId && (
				<Box testId={PEEK_TEST_ID}>
					<NavItem key={peekedItemId} item={allNavItems[peekedItemId]} peek />
				</Box>
			)}
			{!disableItemCustomization && (
				<MoreMenu
					items={allNavItems}
					shortcutItems={shortcuts}
					itemIds={sideNavMenuData.hiddenItemMenuIds}
					shortcutItemIds={sideNavMenuData.hiddenShortcutMenuIds}
					peekedItemId={peekedItemId}
					onPeek={setPeekedItemId}
				/>
			)}
			{postMainElem}
		</Stack>
	);

	return (
		<SideNavDataProvider data={sideNavMenuData}>
			<SideNavigationLayout
				testId={props.testId ?? GLOBAL_SIDE_NAV_TEST_ID}
				preMainElem={topElem}
				postMainElem={postMain}
				bottomElem={bottomElem}
			>
				<MenuList>{sideNavMenuData.visibleItemMenuIds.map(renderNavItem)}</MenuList>
			</SideNavigationLayout>
		</SideNavDataProvider>
	);
}
